import type { ReactNode } from 'react';

import { IconButton, RemoveButton } from './action-buttons.tsx';
import { Button } from './button.tsx';
import { icons } from './icons.ts';

// The item picker's footers (the ItemPicker, `item-picker.tsx`). One at a time: the keys and what
// was just added. Build a list: the picks, each with its own steppers (they live here, not in the
// list's rows, because a listbox option may not hold buttons, INV-13), and the add.

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-xs bg-surface px-1.5 py-px font-sans text-[11px] text-muted shadow-[inset_0_0_0_1px_var(--color-divider)]">
      {children}
    </kbd>
  );
}

const footer = 'flex flex-wrap items-center gap-3 border-t border-divider bg-tile px-4 py-2.5';

export function QuickFooter({
  adding,
  added,
  onDone,
}: {
  adding: boolean;
  added: string | null;
  onDone: () => void;
}) {
  return (
    <div className={`${footer} text-xs text-muted`}>
      <span className="inline-flex items-center gap-1.5">
        <Kbd>↑</Kbd>
        <Kbd>↓</Kbd>Move
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Kbd>Enter</Kbd>Add
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Kbd>Shift Enter</Kbd>Add, keep search
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Kbd>Esc</Kbd>Done
      </span>
      <span className="flex-1" />
      <span role="status" className="inline-flex items-center gap-1.5 text-accent-800">
        {adding ? (
          'Adding…'
        ) : added ? (
          <>
            <icons.added size={14} aria-hidden />
            {added}
          </>
        ) : null}
      </span>
      <Button type="button" variant="quiet" onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

export function ListTray({
  picked,
  nameOf,
  adding,
  added,
  to,
  onBump,
  onDrop,
  onClear,
  onAdd,
  onDone,
}: {
  picked: ReadonlyArray<readonly [string, number]>;
  nameOf: (id: string) => string;
  adding: boolean;
  added: string | null;
  /** Where lines go, e.g. " to Plenary 1", or empty. */
  to: string;
  onBump: (id: string, by: number) => void;
  onDrop: (id: string) => void;
  onClear: () => void;
  onAdd: () => void;
  onDone: () => void;
}) {
  const units = picked.reduce((n, [, qty]) => n + qty, 0);
  return (
    <div className={`${footer} flex-col items-stretch`}>
      {picked.length > 0 ? (
        <ul className="flex flex-col gap-1" aria-label="Picked">
          {picked.map(([id, qty]) => {
            const name = nameOf(id);
            return (
              <li key={id} className="flex items-center gap-1 text-sm">
                <span className="min-w-0 flex-1 truncate">{name}</span>
                <IconButton
                  icon={icons.oneFewer}
                  label={`One fewer ${name}`}
                  onClick={() => onBump(id, -1)}
                />
                <span className="min-w-7 text-center font-heading font-extrabold">{qty}</span>
                <IconButton
                  icon={icons.add}
                  label={`One more ${name}`}
                  onClick={() => onBump(id, 1)}
                />
                <RemoveButton what={`${name} from the list`} onClick={() => onDrop(id)} />
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <p role="status" className="text-sm text-muted">
          {picked.length > 0 ? (
            `${picked.length} ${picked.length === 1 ? 'line' : 'lines'} · ${units} ${units === 1 ? 'unit' : 'units'}`
          ) : added ? (
            <span className="text-accent-800">{added}</span>
          ) : null}
        </p>
        <div className="flex items-center gap-2">
          <Kbd>Ctrl Enter</Kbd>
          <Button type="button" variant="quiet" onClick={onDone}>
            Done
          </Button>
          {/* Shown only with something to add: disabled means "running" here (ADO-06). */}
          {picked.length > 0 ? (
            <>
              <Button type="button" variant="quiet" onClick={onClear}>
                Clear
              </Button>
              <Button type="button" onClick={onAdd} disabled={adding}>
                {adding ? 'Adding…' : `Add (${picked.length})${to}`}
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
